/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ArrayItem,
  RemoveNull,
  RemoveUndefined,
  SMap,
} from '@client/new/helpers/types';
import {
  CouponDiscountType,
  CouponProductType,
  GetCouponsByInfluencerQuery,
  GetSubTiresQuery,
} from 'generated/graphql';
import {
  Discount,
  DiscountsModel,
  OtpBundle,
  SubscriptionPrice,
} from './Discounts.model';
import { SubscriptionType } from '../../collection/domain';
import { LiteCollection } from '../../collections/collections.domain';
import { PATHS } from '@client/routes';

export type Coupon = ArrayItem<
  GetCouponsByInfluencerQuery['getCouponsByInfluencer']
>;

export const mapProduct = (
  coupon: Pick<
    Coupon,
    'productType' | 'subscriptionProviderPriceId' | 'paidProductIds'
  >,
  p: Pick<ToDiscountParams, 'subscriptions' | 'otps' | 'otpBundles'>,
): Discount['product'] => {
  if (coupon.productType === CouponProductType.Subscription) {
    const price = Object.values(p.subscriptions).find(
      (s) => s.providerPriceId === coupon.subscriptionProviderPriceId,
    );
    return {
      type: 'subscription',
      value: { period: price?.period || 'monthly' },
    };
  }

  if (coupon.productType === CouponProductType.OtpBundle) {
    const otpId =
      coupon.paidProductIds && coupon.paidProductIds.length
        ? coupon.paidProductIds[0].toString()
        : '';
    const bundle = p.otpBundles.find((o) => `${o.id}` === otpId);
    return { type: 'otp', value: { otpId, name: bundle?.title || '' } };
  }

  const otpId =
    coupon.paidProductIds && coupon.paidProductIds.length
      ? coupon.paidProductIds[0].toString()
      : '';
  const otp = p.otps.find((o) => `${o.programId}` === otpId);
  return { type: 'otp', value: { otpId, name: otp?.title || '' } };
};

export const mapAmount = (
  coupon: Pick<Coupon, 'discountType' | 'discountAmount' | 'percentOff'>,
): Discount['amount'] => {
  if (coupon.discountType === CouponDiscountType.FixedAmount)
    return { type: 'fixed', value: coupon.discountAmount || 0 };

  return { type: 'percent', value: coupon.percentOff || 0 };
};

export const mapStatus = (
  coupon: Pick<
    Coupon,
    'expiresAt' | 'isActive' | 'maxRedemptions' | 'redemptions'
  >,
): Discount['status'] => {
  if (coupon.expiresAt && new Date(coupon.expiresAt) < new Date())
    return 'Expired';

  if (coupon.maxRedemptions && coupon.redemptions >= coupon.maxRedemptions)
    return 'Limit reached';

  return coupon.isActive ? 'Active' : 'Inactive';
};

const getCheckoutURL = (p: ToDiscountParams & { coupon: Coupon }) => {
  const toUrl = () => {
    if (p.coupon.productType === CouponProductType.Subscription)
      return p.subsCheckoutURL ? `${p.subsCheckoutURL}` : '';

    const otpId =
      p.coupon.paidProductIds && p.coupon.paidProductIds.length
        ? p.coupon.paidProductIds[0].toString()
        : '';

    if (p.coupon.productType === CouponProductType.OtpBundle) {
      return p.otpBundles.find((o) => `${o.id}` === otpId)?.checkoutPage?.url;
    }

    const url = p.otps.find((o) => `${o.programId}` === otpId)?.salesPageUrl;
    return url + '/checkout';
  };

  return `${toUrl()}?promo=${p.coupon.code}`;
};

export type ToDiscountParams = {
  subscriptions: DiscountsModel['subscriptionMap'];
  subsCheckoutURL: string;
  otps: Array<LiteCollection>;
  otpBundles: Array<OtpBundle>;
};
export const toDiscount =
  (p: ToDiscountParams) =>
  (coupon: Coupon): Discount => {
    return {
      detailURL: PATHS.PROMOTIONS_DETAILS(
        coupon.influencerId,
        parseInt(coupon.id, 10),
      ), // TODO: move to view model
      name: coupon.name,
      code: coupon.code,
      discountId: parseInt(coupon.id, 10),
      product: mapProduct(coupon, p),
      amount: mapAmount(coupon),
      createdAt: new Date(coupon.createdAt).getTime(),
      expiresAt: coupon.expiresAt ? new Date(coupon.expiresAt).getTime() : null,
      redemptionsLimit: coupon.maxRedemptions || null,
      redemptions: coupon.redemptions,
      status: mapStatus(coupon),
      providerCouponId: coupon.providerCouponId || '',
      url: getCheckoutURL({ ...p, coupon }),
    };
  };

type MapCollectionConfig<T> = {
  label: string;
  mapper: (v: any) => T | null;
  noError: boolean;
};

export const mapCollection =
  <T>(defaultConfig: MapCollectionConfig<T>) =>
  (
    vs: Array<any> | undefined | null,
    _config: Partial<MapCollectionConfig<T>> = {},
  ): Array<T> => {
    if (!vs) return [];
    const config = { ...defaultConfig, ..._config };
    return vs.reduce((acc, v) => {
      try {
        const d = config.mapper(v);
        return d !== null ? [...acc, d] : acc;
      } catch (e) {
        if (!config.noError) console.error(`Error mapping ${config.label}`, e);
        return acc;
      }
    }, [] as T[]);
  };

export const toDiscounts = (p: ToDiscountParams) =>
  mapCollection({
    mapper: toDiscount(p),
    label: 'Discount',
    noError: false,
  });

export type SubscriptionTire = RemoveNull<
  ArrayItem<RemoveNull<GetSubTiresQuery['subscriptionTierByInfluencer']>>
>;

const toSubscriptionPrice = (
  price: ArrayItem<RemoveUndefined<SubscriptionTire['prices']>>,
): SubscriptionPrice | null => {
  if (!price) return null;

  const period: SubscriptionType =
    price.billingCycleInterval === 'Month' ? 'monthly' : 'annual';

  return {
    period,
    providerPriceId: price.providerPriceId ?? '',
    price: price.price,
  };
};

export const mapSubscriptionTierPrices = (
  tier: SubscriptionTire | null | undefined,
): Array<SubscriptionPrice> => {
  if (!tier || !tier.prices) return [];
  return mapCollection({
    mapper: toSubscriptionPrice,
    label: 'Subscription Price',
    noError: false,
  })(tier.prices);
};

export type CreatorType = 'standard' | 'ppc' | 'playbook' | 'limited';

const visibilityMap: SMap<CreatorType> = {
  exclusive: 'ppc',
  limited: 'limited',
  premium: 'standard',
};

export const toCreatorType = (c: {
  id?: number;
  visibility?: string;
}): CreatorType =>
  c.id === 0 ? 'playbook' : visibilityMap[c.visibility || ''] || 'standard';
